/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, HFlow, Spinner, Text, Theme, Tooltip, useTheme, VFlow } from 'bold-ui'
import { AccordionCompositeItem } from 'components/accordion/AccordionCompositeItem'
import useSession from 'components/auth/useSession'
import { resolveName, usePecField } from 'components/form/final-form/hooks/useField'
import { confirm } from 'components/modals/confirm'
import { ButtonLink, PrivateRoute } from 'components/route'
import { useFlags } from 'config/useFlagsContext'
import { isAfter } from 'date-fns'
import { AcessoCbo, ReceitaMedicamento } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useServerTime } from 'hooks/useServerTime'
import React, { useCallback, useState } from 'react'
import { Accordion } from 'react-accessible-accordion'
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays'
import { Switch, useHistory, useRouteMatch } from 'react-router'
import { metaPath } from 'util/metaPath'
import { meta, SoapState } from 'view/atendimentos/atendimento-individual/model'
import { grupoCboPrescricao } from 'view/atendimentos/detail/soap/plano/acessos'
import {
  ADICIONAR_PRESCRICAO_SUB_PATH,
  GrupoReceitaMedicamento,
  MedicamentoFormModel,
} from 'view/atendimentos/detail/soap/plano/prescricao-medicamento/model-prescricao'
import { convertReceitaMedicamentoToMedicamento } from 'view/atendimentos/detail/soap/plano/prescricao-medicamento/utils/converter-prescricao'

import InterromperTratamentoModal, { MedicamentoInterromperFormModel } from './InterromperTratamentoModal'

interface MedicamentosAccordionProps {
  loading: boolean
  grupos: GrupoReceitaMedicamento[]
  podeInterromperOuRenovarMedicamento: boolean
  dataAtendimento: Instant
  setAlertState: React.Dispatch<React.SetStateAction<AlertInlineProps>>
  renderItemBody: (receita: ReceitaMedicamento) => JSX.Element
  renderItemHeader: (receita: ReceitaMedicamento, dataInicioGrupo: string, hasInterrompidoAgora: boolean) => JSX.Element
  renderGroupedBody: (grupo: ReceitaMedicamento[]) => JSX.Element
}

interface AlertInlineProps {
  visible: boolean
  message: string
}

const path = metaPath<SoapState>()

function MedicamentosAccordion(props: MedicamentosAccordionProps) {
  const {
    loading,
    grupos,
    podeInterromperOuRenovarMedicamento,
    dataAtendimento,
    setAlertState,
    renderItemBody,
    renderItemHeader,
    renderGroupedBody,
  } = props
  const { analytics } = useFirebase()
  const { getServerTimeNow } = useServerTime()
  const match = useRouteMatch()
  const [medicamentoToInterrupt, setMedicamentoToInterrupt] = useState<ReceitaMedicamento>()
  const {
    data: { acesso },
    hasCboAuth,
  } = useSession({ fetchPolicy: 'cache-only' })

  const { RENOVACAO_PRESCRICAO_ENABLED } = useFlags()

  const isNutricionistaOuFarmaceutico = hasCboAuth([AcessoCbo.NUTRICIONISTA, AcessoCbo.FARMACEUTICO])

  const theme = useTheme()
  const styles = createStyles(theme)
  const history = useHistory()

  const {
    input: { onChange: setMedicamentosToReplicate },
  } = usePecField({ name: meta.plano.prescricaoMedicamento.replicateStorage })

  const {
    input: { value: medicamentos },
  } = usePecField({ name: meta.plano.prescricaoMedicamento.medicamentos })

  const receitasRenovadas = new Set(
    medicamentos
      ? medicamentos.map((medicamento: MedicamentoFormModel) => medicamento.receitaUsoContinuoGrupoId).filterNotNull()
      : []
  )

  const handleExecutarRenovacao = useCallback(
    (receita: ReceitaMedicamento, isVersaoAnterior: boolean) => {
      const medicamento: MedicamentoFormModel = convertReceitaMedicamentoToMedicamento(
        receita,
        isVersaoAnterior,
        true,
        dataAtendimento,
        true
      )
      setMedicamentosToReplicate([medicamento])

      const newPath = match.url.replace('/medicamentos-ativos-concluidos', ADICIONAR_PRESCRICAO_SUB_PATH)
      history.push(newPath)
    },
    [dataAtendimento, history, match.url, setMedicamentosToReplicate]
  )
  const handleRenovar = useCallback(
    (receita: ReceitaMedicamento) => {
      analytics.logEvent('click_button_renovar_prescricao_modal')
      const isVersaoAnterior = receita.atendProf.versaoAnterior

      if (isVersaoAnterior) {
        confirm({
          type: 'primary',
          title: 'Deseja renovar este medicamento?',
          body:
            'Este medicamento foi prescrito em versão anterior do sistema. Para concluir sua renovação será necessário completar informações. Deseja continuar?',
          onConfirm: () => {
            analytics.logEvent(`replicar_medicamento_versao_anterior`)
            handleExecutarRenovacao(receita, true)
          },
          confirmLabel: 'Continuar',
          cancelLabel: 'Cancelar',
          depthLevel: 4,
          manageOverflow: true,
        })()
      } else {
        handleExecutarRenovacao(receita, false)
      }
    },
    [analytics, handleExecutarRenovacao]
  )

  const renderFieldArray = useCallback(
    (fieldArrayRenderProps: FieldArrayRenderProps<MedicamentoInterromperFormModel, any>) => {
      const {
        fields: { push, remove, value },
      } = fieldArrayRenderProps

      const onClearItem = () => {
        setMedicamentoToInterrupt(undefined)
      }

      const handleOnSubmit = (interrupcao: MedicamentoInterromperFormModel) => {
        push(interrupcao)
        setAlertState({ visible: true, message: 'Tratamento interrompido com sucesso.' })
      }

      const handleOnCancel = (interrupcao: MedicamentoInterromperFormModel) => {
        const item = value?.find((item) => item.id === interrupcao.id)
        if (item) {
          const index = value.indexOf(item)
          confirm({
            title: 'Deseja cancelar a interrupção do tratamento?',
            confirmLabel: 'Confirmar',
            cancelLabel: 'Cancelar',
            depthLevel: 5,
            manageOverflow: false,
            onConfirm: () => {
              remove(index)
              onClearItem()
              setAlertState({ visible: true, message: 'Interrupção cancelada com sucesso.' })
            },
          })()
        }
        analytics.logEvent('click_button_cancelar_interrupcao')
      }

      const handleInterromperTratamento = (receita: ReceitaMedicamento, podeInterromperTratamento: boolean) => {
        if (!isNutricionistaOuFarmaceutico || podeInterromperTratamento) {
          setMedicamentoToInterrupt(receita)
        }

        analytics.logEvent('click_button_interromper_tratamento')
      }

      const renderItemButton = (receita: ReceitaMedicamento) => {
        if (!podeInterromperOuRenovarMedicamento) return null

        const item = value?.find((item) => item.id === receita.id)
        const lotacaoAtualQuePrescreveu = receita.atendProf.lotacao.id === acesso.id
        const podeInterromperTratamento = isNutricionistaOuFarmaceutico && lotacaoAtualQuePrescreveu
        const isUsoContinuo = receita.usoContinuo
        const isGrouped =
          receitasRenovadas.has(receita.receitaUsoContinuoGrupo?.id) || receitasRenovadas.has(receita.id)
        const isRenovado = isUsoContinuo && isGrouped
        const isProfissionalResponsavel = !(isNutricionistaOuFarmaceutico && !lotacaoAtualQuePrescreveu)
        const tooltipText = getButtonTooltipText(isProfissionalResponsavel, isRenovado)
        const prescricaoInicial = receita.receitaUsoContinuoGrupo ?? receita
        const tratamentoNaoIniciado = isAfter(prescricaoInicial.dtInicioTratamento, getServerTimeNow())
        const tooltipRenovarText = tratamentoNaoIniciado
          ? 'Não é possível renovar a prescrição de um tratamento ainda não iniciado'
          : ''

        return (
          <div css={styles.itemButtonContainer}>
            {item ? (
              <Button
                size='small'
                onClick={() => handleOnCancel(item)}
                data-cy='MedicamentoAtivosConcluidos.BotaoCancelarInterrupcao'
              >
                Cancelar interrupção
              </Button>
            ) : (
              <HFlow hSpacing={0.5}>
                <Tooltip text={tooltipText}>
                  <ButtonLink
                    size='small'
                    to={`${match.url}/interromper-tratamento`}
                    onClick={() => handleInterromperTratamento(receita, podeInterromperTratamento)}
                    disabled={!isProfissionalResponsavel || isRenovado}
                    data-cy='MedicamentoAtivosConcluidos.InterromperButton'
                  >
                    Interromper
                  </ButtonLink>
                </Tooltip>
                {RENOVACAO_PRESCRICAO_ENABLED && isUsoContinuo && (
                  <Tooltip text={tooltipRenovarText}>
                    <Button
                      size='small'
                      kind='primary'
                      skin='outline'
                      style={styles.renovarButton}
                      onClick={() => handleRenovar(receita)}
                      disabled={tratamentoNaoIniciado}
                    >
                      Renovar
                    </Button>
                  </Tooltip>
                )}
              </HFlow>
            )}
          </div>
        )
      }

      return (
        <div>
          <Accordion allowZeroExpanded allowMultipleExpanded>
            <VFlow vSpacing={0.5}>
              {loading && (
                <div css={styles.loadingContainer}>
                  <Spinner />
                  {'Carregando resultados'}
                </div>
              )}
              {!loading && grupos.isEmpty() && <Text style={styles.emptyText}>Nenhum medicamento em uso</Text>}
              {grupos.map(({ prescricoes }) => {
                const prescricaoPrincipal = prescricoes.first()
                const prescricaoMaisAntiga = prescricoes.last()
                const hasInterrompidoAgora = value?.some(
                  (interrompidoAgora) => interrompidoAgora.id === prescricaoPrincipal.id
                )
                return (
                  <AccordionCompositeItem
                    key={prescricaoPrincipal.receitaUsoContinuoGrupo?.id ?? prescricaoPrincipal.id}
                    header={renderItemHeader(
                      prescricaoPrincipal,
                      prescricaoMaisAntiga.dtInicioTratamento,
                      hasInterrompidoAgora
                    )}
                    headerButtons={renderItemButton(prescricaoPrincipal)}
                    body={prescricoes.length > 1 ? renderGroupedBody(prescricoes) : renderItemBody(prescricaoPrincipal)}
                    dataTestid='MedicamentosAccordion'
                  />
                )
              })}
            </VFlow>
          </Accordion>
          <Switch>
            <PrivateRoute
              path={`${match.url}/interromper-tratamento`}
              permission={grupoCboPrescricao.interromperTratamento}
            >
              <InterromperTratamentoModal
                receita={medicamentoToInterrupt}
                onSubmit={handleOnSubmit}
                onClear={onClearItem}
              />
            </PrivateRoute>
          </Switch>
        </div>
      )
    },
    [
      loading,
      styles.loadingContainer,
      styles.emptyText,
      styles.itemButtonContainer,
      styles.renovarButton,
      grupos,
      match.url,
      medicamentoToInterrupt,
      setAlertState,
      analytics,
      isNutricionistaOuFarmaceutico,
      podeInterromperOuRenovarMedicamento,
      acesso.id,
      receitasRenovadas,
      getServerTimeNow,
      RENOVACAO_PRESCRICAO_ENABLED,
      handleRenovar,
      renderItemHeader,
      renderGroupedBody,
      renderItemBody,
    ]
  )

  return (
    <FieldArray<MedicamentoInterromperFormModel>
      name={resolveName(path.medicamentosInterromper)}
      render={renderFieldArray}
    />
  )
}

export default React.memo(MedicamentosAccordion)

const createStyles = (theme: Theme) => ({
  loadingContainer: css`
    color: ${theme.pallete.primary.main};
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    & > *:first-of-type {
      margin-right: 0.5rem;
    }
  `,
  renovarButton: css`
    background-color: ${theme.pallete.surface.main};
  `,
  itemButtonContainer: css`
    flex-shrink: 0;
    min-width: 11rem;
    display: flex;
    justify-content: flex-end;
  `,
  emptyText: css`
    color: ${theme.pallete.text.secondary};
    font-style: italic;
    text-align: center;
  `,
})

const getButtonTooltipText = (isProfissionalResponsavel: boolean, isRenovado: boolean): string => {
  return isProfissionalResponsavel
    ? isRenovado && 'Não é possível interromper uma prescrição renovada no mesmo atendimento'
    : 'O tratamento só pode ser interrompido pelo profissional que o iniciou'
}
